import { memo, useCallback, useEffect, useMemo, useSyncExternalStore, useState } from 'react';
import { Popover, Transition } from '@headlessui/react';
import PlayerMenuTooltip from '../../../PlayerMenus/PlayerMenuTooltip';
import { updateTextElementOutline, removeTextElementOutline, getTextElementOutline } from './textElementPopoverUtils';
import { getTextElementById } from '../utils';
import { ColorPicker } from '@/components/atoms/ColorPicker/ColorPicker';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { brandKit } from '@/stores/brandKit';
import { classnames } from '@/libs/utils';
import Toggle from '@/components/atoms/Toggle';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import Icon from '@/components/atoms/Icon';

function TextElementOutlinePopover({ textElementId }: { textElementId: string }) {
  const { clipId, clipData } = useClipsContext();
  const brandKitStore = useSyncExternalStore(brandKit.subscribe, brandKit.getSnapshot);
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const usePlatformBrandKit = !!featureFlags[FeatureFlagKeys.Use_CL_Platform_Brandkit];
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [lastOutlineState, setLastOutlineState] = useState<{ width: number; color: string } | null>(null);

  useEffect(() => {
    setLastOutlineState(null);
  }, [textElementId]);

  const selectedElement = useMemo(() => {
    return getTextElementById(clipData, textElementId);
  }, [clipData, textElementId]);

  const outlineToggleEnabled = useMemo(() => {
    return selectedElement ? !!selectedElement.style?.WebkitTextStrokeWidth : false;
  }, [selectedElement]);

  const { width, color } = useMemo(() => {
    return getTextElementOutline(selectedElement);
  }, [selectedElement]);

  const onToggleOutline = useCallback(() => {
    if (outlineToggleEnabled) {
      setLastOutlineState({ width, color });
      removeTextElementOutline(clipId, clipData, textElementId);
    } else {
      const restoredWidth = lastOutlineState?.width ?? (width > 0 ? width : 2);
      const textColor = selectedElement.style?.color;
      const defaultColor = textColor && textColor !== color ? textColor : color;
      const restoredColor = lastOutlineState?.color ?? defaultColor;
      updateTextElementOutline(clipId, clipData, textElementId, restoredWidth, restoredColor);
    }
  }, [outlineToggleEnabled, clipId, clipData, textElementId, selectedElement, width, color, lastOutlineState]);

  const updateOutline = useCallback(
    (newValues: { width?: number; color?: string }) => {
      const updatedWidth = newValues.width ?? width;
      const updatedColor = newValues.color ?? color;
      updateTextElementOutline(clipId, clipData, textElementId, updatedWidth, updatedColor);
    },
    [width, color, clipId, clipData, textElementId]
  );

  const onWidthChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateOutline({ width: Number(e.target.value) });
    },
    [updateOutline]
  );

  const onOutlineColorChange = useCallback(
    (value?: string) => {
      if (value) {
        updateOutline({ color: value });
      }
    },
    [updateOutline]
  );

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            data-testid="text-element-outline-button"
            className={classnames(
              'group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100',
              open ? 'bg-slate-100' : 'hover:bg-slate-50'
            )}
          >
            <div className="flex w-10 cursor-pointer items-center justify-center rounded-md">
              <Icon name="IconScribble" size={16} />
              <PlayerMenuTooltip content="Outline" />
            </div>
          </Popover.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
            afterLeave={() => setIsColorPickerOpen(false)}
          >
            <Popover.Panel className="absolute z-50 mt-1 w-64 translate-y-1 space-y-5">
              <div className="rounded-lg border bg-white shadow-lg">
                {usePlatformBrandKit && isColorPickerOpen ? (
                  <ColorPicker
                    defaultColorsLabel="Outline Color"
                    onChange={onOutlineColorChange}
                    hex={color}
                    inline
                    onBack={() => setIsColorPickerOpen(false)}
                  />
                ) : (
                  <div className="space-y-4 p-4">
                    {/* Enable/Disable Outline */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Outline</span>
                      <Toggle on={outlineToggleEnabled} onToggle={onToggleOutline} />
                    </div>

                    {outlineToggleEnabled && (
                      <>
                        {/* Width */}
                        <div className="grid grid-cols-12 items-center justify-between gap-2">
                          <span className="col-span-4 text-sm font-medium">Width</span>
                          <span className="col-span-8 flex items-center gap-2">
                            <input
                              type="range"
                              min={0}
                              max={10}
                              value={width}
                              step={1}
                              onChange={onWidthChange}
                              className="h-1 w-full cursor-pointer appearance-none rounded-xl bg-slate-300"
                            />
                            <span className="ml-1 shrink-0 text-right text-2xs tabular-nums text-slate-400">
                              {width}px
                            </span>
                          </span>
                        </div>

                        {/* Stroke Color */}
                        <div className="grid grid-cols-12 items-center justify-between gap-2">
                          <span className="col-span-4 text-sm font-medium">Color</span>
                          <ColorPicker
                            defaultColors={brandKitStore.colors}
                            defaultColorsLabel="Outline Color"
                            onChange={onOutlineColorChange}
                            hex={color}
                            onOpen={usePlatformBrandKit ? () => setIsColorPickerOpen(true) : undefined}
                            children={
                              <div className="flex size-full items-center justify-between bg-white p-2">
                                <div
                                  className="size-6 rounded border"
                                  style={{
                                    background: color
                                  }}
                                />
                                <span className="font-mono text-xs text-slate-400">{color}</span>
                              </div>
                            }
                            classNames="relative col-span-8 flex w-full items-center justify-between rounded-md border overflow-hidden"
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default memo(TextElementOutlinePopover);
